import { useEffect, useState } from "react"
import {
  Activity,
  BookUser,
  Building2,
  Cctv,
  ChevronsLeft,
  ChevronsRight,
  ClipboardCheck,
  Clock,
  Cpu,
  LayoutDashboard,
  MessageSquare,
  Route,
  ScrollText,
  Share2,
  User2,
  Zap,
  type LucideIcon,
} from "lucide-react"

import type { Permission } from "@/config/permissions"
import { cn } from "@/lib/utils"

import { Authorized } from "../auth/authorized"
import { Button } from "../ui/button"
import { Separator } from "../ui/separator"
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip"
import ActiveLink from "./active-link"

// Types
interface NavigationItem {
  to: string
  icon: LucideIcon
  label: string
  permission?: Permission
  hideInProduction?: boolean
}

interface NavigationSection {
  title: string
  items: NavigationItem[]
}

// Navigation Configuration
const NAVIGATION_CONFIG: {
  dashboard: NavigationItem
  sections: NavigationSection[]
} = {
  dashboard: {
    to: "/dashboard",
    icon: LayoutDashboard,
    label: "Dashboard",
  },
  sections: [
    {
      title: "Manage",
      items: [
        {
          to: "/manage/facilities",
          icon: Building2,
          label: "Facilities",
          permission: "view_facility",
        },
        {
          to: "/manage/pathways",
          icon: Route,
          label: "Pathways",
          permission: "view_pathway",
        },
        {
          to: "/manage/adt-trigger-rules",
          icon: Zap,
          label: "Transition Care",
          permission: "view_patientadtevent",
          hideInProduction: true,
        },
        {
          to: "/query-analytics",
          icon: MessageSquare,
          label: "Query Analytics",
          permission: "view_patient",
          hideInProduction: true,
        },
        {
          to: "/manage/users",
          icon: User2,
          label: "Staff",
          permission: "view_user",
        },
        {
          to: "/manage/patients",
          icon: BookUser,
          label: "Patients",
          permission: "view_patient",
        },
        {
          to: "/manage/devices",
          icon: Cctv,
          label: "Devices",
          permission: "view_device",
        },
        {
          to: "/manage/referrals",
          icon: Share2,
          label: "Referrals",
          permission: "view_referral",
        },
      ],
    },
    {
      title: "Reports",
      items: [
        {
          to: "/reports/biometrics",
          icon: Activity,
          label: "Biometrics",
        },
        {
          to: "/reports/billing",
          icon: Clock,
          label: "Billing",
        },
        {
          to: "/reports/compliance",
          icon: ClipboardCheck,
          label: "Compliance",
        },
      ],
    },
    {
      title: "System",
      items: [
        {
          to: "/system/audit-logs",
          icon: ScrollText,
          label: "Audit Logs",
          permission: "view_logentry",
        },
        {
          to: "/system/ai-roadmap",
          icon: Cpu,
          label: "AI Roadmap",
          permission: "view_ai_roadmap",
        },
      ],
    },
  ],
}

// Components
function NavigationLink({
  item,
  isCollapsed,
}: {
  item: NavigationItem
  isCollapsed: boolean
}) {
  const Icon = item.icon

  const linkContent = (
    <div className="group" role="menuitem" aria-label={item.label}>
      <ActiveLink
        to={item.to}
        activeClassName="bg-brand text-white hover:bg-brand/90 hover:text-white"
      >
        <Icon
          className={cn(
            "size-4 transition-transform duration-200 group-hover:scale-110",
            !isCollapsed && "mr-2"
          )}
        />
        {!isCollapsed && <span>{item.label}</span>}
      </ActiveLink>
    </div>
  )

  if (isCollapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
        <TooltipContent side="right">
          <p>{item.label}</p>
        </TooltipContent>
      </Tooltip>
    )
  }

  return linkContent
}

function SectionHeader({
  title,
  isCollapsed,
}: {
  title: string
  isCollapsed: boolean
}) {
  if (isCollapsed) {
    return <Separator className="my-3" />
  }

  return (
    <h2 className="mb-2 mt-5 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
      {title}
    </h2>
  )
}

export function Sidebar() {
  const [isCollapsed, setIsCollapsed] = useState(false)

  // Load collapsed state from localStorage on mount
  useEffect(() => {
    const savedState = localStorage.getItem("sidebar-collapsed")
    if (savedState !== null) {
      setIsCollapsed(savedState === "true")
    }
  }, [])

  // Save collapsed state to localStorage when it changes
  const toggleCollapse = () => {
    setIsCollapsed(prev => {
      const newState = !prev
      localStorage.setItem("sidebar-collapsed", String(newState))
      return newState
    })
  }

  return (
    <div
      className={cn(
        "flex flex-col border-r bg-muted/20 py-4 transition-all duration-300",
        isCollapsed ? "w-16 min-w-16 px-2" : "w-52 px-4"
      )}
      style={
        isCollapsed ? { minWidth: "4rem", width: "4rem" } : { width: "12rem" }
      }
      role="navigation"
      aria-label="Main navigation"
    >
      {/* Logo and Toggle */}
      <div
        className={cn(
          "flex items-center pb-4",
          isCollapsed ? "flex-col gap-2" : "justify-between"
        )}
      >
        {!isCollapsed && (
          <div className="flex w-full justify-center">
            <img
              src="/light-logo.png"
              alt="Health Logo"
              className="hidden h-12 w-32 transition-all duration-200 hover:scale-105 lg:block"
            />
          </div>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleCollapse}
          className={cn(
            "h-8 w-8 shrink-0 transition-all",
            isCollapsed && "mx-auto"
          )}
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {isCollapsed ? (
            <ChevronsRight className="size-4" />
          ) : (
            <ChevronsLeft className="size-4" />
          )}
        </Button>
      </div>

      <Separator className="mb-4" />

      {/* Dashboard Link */}
      <div className="flex flex-col gap-y-1" role="menu">
        <NavigationLink
          item={NAVIGATION_CONFIG.dashboard}
          isCollapsed={isCollapsed}
        />
      </div>

      {/* Navigation Sections */}
      {NAVIGATION_CONFIG.sections.map(section => (
        <div key={section.title}>
          <SectionHeader title={section.title} isCollapsed={isCollapsed} />
          <div className="flex flex-col gap-y-1" role="menu">
            {section.items.filter(item => !(item.hideInProduction && ["production", "prod"].includes(import.meta.env.VITE_APP_ENV ?? ""))).map(item => {
              const linkElement = (
                <NavigationLink item={item} isCollapsed={isCollapsed} />
              )

              if (item.permission) {
                return (
                  <Authorized key={item.to} permissions={[item.permission]}>
                    {linkElement}
                  </Authorized>
                )
              }

              return <div key={item.to}>{linkElement}</div>
            })}
          </div>
        </div>
      ))}
    </div>
  )
}
